import { DOD_ID_LETTER_DIGIT_PATTERN } from '@ths/shared/utils';
import { HotelForm as Form } from 'hotel/HotelForm';
import { Field } from 'react-final-form';
import Modal from 'react-modal';

import { popToast } from 'utilities/popToast';
import removeTypename from 'utilities/removeTypename';

import {
  ClientTeamMember,
  useCreateClientTeamMemberMutation,
} from '../generated/graphql';
import { useAppSelector } from '../store/store';
import { GuestFieldValues } from '../utilities/guestFieldMapping';
import { parseText } from '../utilities/parsers';
import { required } from '../utilities/validators';

import { PrimaryButton, SecondaryButton } from './Button';
import GuestRequiredFields from './GuestRequiredFields';
import TextField from './Inputs/FormInputs/TextField';

// Set the app element for accessibility
if (typeof window !== 'undefined') {
  Modal.setAppElement('#root');
}

export type CreatedGuestValues = Omit<ClientTeamMember, '__typename'>;
export type CreateGuestFormValues = GuestFieldValues;

interface CreateGuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGuestCreated: (guest: CreatedGuestValues) => void;
  initialValues?: Partial<CreateGuestFormValues>;
}

const CreateGuestModal: React.FC<CreateGuestModalProps> = ({
  isOpen,
  onClose,
  onGuestCreated,
  initialValues: providedInitialValues,
}) => {
  const clientSubgroups = useAppSelector(
    (state) => state.enums.clientSubgroups,
  );

  const [createClientTeamMember] = useCreateClientTeamMemberMutation({
    onCompleted: () => {
      popToast(`New member created!`);
    },
    onError: (error) => {
      popToast(error.message, { type: 'error' });
    },
    refetchQueries: ['rosterGuestList'],
  });

  const handleSubmit = async (values: CreateGuestFormValues) => {
    const { edipi, ...restValues } = values;

    let input;
    const validSubgroups = clientSubgroups?.map((s) => s.value) || [];

    if (edipi) {
      // Check if it's a client subgroup (case-insensitive)
      const upperEdipi = edipi.toUpperCase();
      const matchingSubgroup = validSubgroups.find(
        (subgroup) => subgroup.toUpperCase() === upperEdipi,
      );

      if (matchingSubgroup) {
        // Use the properly cased subgroup from the backend
        input = { ...restValues, clientSubgroup: matchingSubgroup };
      } else {
        // For TXSG format DOD IDs, convert to uppercase
        if (DOD_ID_LETTER_DIGIT_PATTERN.test(edipi)) {
          input = { ...restValues, edipi: edipi.toUpperCase() };
        } else {
          // Keep as-is for numeric DOD IDs
          input = { ...restValues, edipi };
        }
      }
    } else {
      input = { ...restValues, edipi };
    }

    const request = await createClientTeamMember({
      variables: {
        input,
      },
    });
    const createdGuest = request.data?.createClientTeamMember;
    if (createdGuest) {
      onGuestCreated(removeTypename(createdGuest));
    }
    onClose();
  };

  const initialValues: Partial<CreateGuestFormValues> = {
    firstName: '',
    lastName: '',
    ...providedInitialValues,
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="relative mx-auto mt-20 max-w-md rounded-lg bg-white p-6 shadow-xl"
      overlayClassName="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 px-4"
      closeTimeoutMS={200}
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Create new guest
        </h2>
      </div>

      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, invalid }) => (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Field
                  name="firstName"
                  component={TextField}
                  labelText="First name"
                  parse={parseText}
                  validate={required}
                />
              </div>
              <div>
                <Field
                  name="lastName"
                  component={TextField}
                  labelText="Last name"
                  parse={parseText}
                  validate={required}
                />
              </div>
            </div>

            <GuestRequiredFields />

            <div className="flex gap-2 pt-4">
              <SecondaryButton
                text="Cancel"
                onClick={onClose}
                type="button"
                className="flex-1"
              />
              <PrimaryButton
                text={
                  submitting ? 'Creating...' : 'Save and add guest to this room'
                }
                type="submit"
                disabled={submitting || invalid}
                className="flex-1"
              />
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default CreateGuestModal;
